import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/api";

const NavLink = ({ children, to, onClick, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    onClick={onClick}
    className="group relative font-mono text-xs uppercase tracking-[0.2em] text-haze transition-colors hover:text-white"
  >
    {children}
    <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-lime transition-transform duration-300 group-hover:scale-x-100" />
  </Link>
);

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();
  const goShop = (e) => {
    if (pathname !== "/") return;
    e.preventDefault();
    scrollToId("shop");
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-violet/30 bg-ink/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:h-20 md:px-10">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="flex items-center gap-5 md:gap-10">
          <NavLink testId="nav-shop-link" to="/#shop" onClick={goShop}>Shop</NavLink>
          <NavLink testId="nav-about-link" to="/about">About</NavLink>
          <motion.button
            data-testid="cart-drawer-toggle"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.92 }}
            onClick={() => setOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center rounded-full bg-lime text-ink"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={2.4} />
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  data-testid="cart-count-badge"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-pink px-1 font-mono text-[11px] font-bold text-white"
                >
                  <motion.span key={count} initial={{ scale: 1.8 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 18 }}>
                    {count}
                  </motion.span>
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </nav>
      </div>
    </motion.header>
  );
};
